import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { FaTrash, FaPlus } from 'react-icons/fa';
import {
  getReport,
  getDetails,
  CreateSiteVisitReport,
  UpdateSiteVisitReport,
  ArchiveSiteVisitReport,
} from 'src/request/Reports';
import { ReportLayout } from './components';

const getTodayDateString = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const SiteVisitV2 = (props) => {
  const [siteVisits, setSiteVisits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(0);
  const [modalMode, setModalMode] = useState('Add');
  const [reportLogId, setReportLogId] = useState(-1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [attachmentList, setAttachmentList] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [removedReportIds, setRemovedReportIds] = useState([]);
  const [weatherData, setWeatherData] = useState([]);

  const [reportItems, setReportItems] = useState([
    {
      report_id: -1,
      date: getTodayDateString(),
      purpose: '',
      event: '',
    },
  ]);

  useEffect(() => {
    fetchSiteVisits(0);
  }, [props.id]);

  const fetchSiteVisits = (selectedStatus) => {
    setIsLoading(true);
    getReport(props.id, 'site_visit', selectedStatus)
      .then((res) => {
        if (res.data?.status == 1) {
          setSiteVisits(res.data.response || []);
        } else {
          Swal.fire({
            icon: 'error',
            title: res.data?.response || 'Error',
            text: res.data?.description || 'Failed to fetch site visit reports',
            confirmButtonColor: '#23303D',
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleOpenAddModal = () => {
    setModalMode('Add');
    setAttachmentFiles([]);
    setAttachmentList([]);
    setRemovedAttachmentIds([]);
    setRemovedReportIds([]);
    setWeatherData([]);
    setReportItems([
      {
        report_id: -1,
        date: getTodayDateString(),
        purpose: '',
        event: '',
        report_log_id: reportLogId,
      },
    ]);
    setIsModalOpen(true);
  };

  const handleOpenReportDetails = (selectedReportLogId, mode = 'View') => {
    setReportLogId(selectedReportLogId);
    setModalMode(mode);
    setAttachmentFiles([]);
    setAttachmentList([]);
    setRemovedAttachmentIds([]);
    setRemovedReportIds([]);
    setWeatherData([]);
    getDetails('site_visit', selectedReportLogId).then((res) => {
      if (res.data?.status == 1) {
        const responseData = res.data?.response;
        const entries = Array.isArray(responseData)
          ? responseData
          : responseData?.entries || [];

        const mappedItems = entries.length > 0
          ? entries.map((entry) => ({
              report_id: entry.report_id || '',
              date: entry.date
                ? (entry.date.includes('T') ? entry.date.split('T')[0] : entry.date.split(' ')[0])
                : '',
              purpose: entry.purpose || '',
              event: entry.event || '',
              report_log_id: entry.report_log_id || selectedReportLogId,
            }))
          : [
              {
                report_id: -1,
                date: getTodayDateString(),
                purpose: '',
                event: '',
                report_log_id: selectedReportLogId,
              },
            ];

        setReportItems(mappedItems);

        const rawAttachments = Array.isArray(responseData?.attachments)
          ? responseData.attachments
          : responseData?.[0]?.attachments || [];

        const existingAttachments = rawAttachments.map((att) => ({
          id: att.id,
          name: att.filename || att.name,
          link: att.url || att.link,
          isExisting: true,
        }));
        setAttachmentList(existingAttachments);

        setWeatherData(responseData?.weather_data || []);
        setIsModalOpen(true);
      } else {
        Swal.fire({
          icon: 'error',
          title: res.data?.response || 'Error',
          text: res.data?.description || 'Failed to fetch report details',
          confirmButtonColor: '#23303D',
        });
      }
    });
  };

  const handleAddReportRow = () => {
    setReportItems([
      ...reportItems,
      {
        report_id: -1,
        date: getTodayDateString(),
        purpose: '',
        event: '',
        report_log_id: reportLogId,
      },
    ]);
  };

  const handleDateChange = (e, index) => {
    const updated = [...reportItems];
    updated[index].date = e.target.value;
    setReportItems(updated);
  };

  const handlePurposeChange = (e, index) => {
    const updated = [...reportItems];
    updated[index].purpose = e.target.value;
    setReportItems(updated);
  };

  const handleEventChange = (e, index) => {
    const updated = [...reportItems];
    updated[index].event = e.target.value;
    setReportItems(updated);
  };

  const handleRemoveReportRow = (index) => {
    const itemToRemove = reportItems[index];
    if (itemToRemove?.report_id && Number(itemToRemove.report_id) > 0) {
      setRemovedReportIds((prev) => [...prev, itemToRemove.report_id]);
    }
    const updated = [...reportItems];
    updated.splice(index, 1);
    setReportItems(updated);
  };

  const handleUploadFiles = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const newFiles = [...attachmentFiles, ...selectedFiles];
    setAttachmentFiles(newFiles);

    const newDisplayList = selectedFiles.map((file) => ({
      name: file.name,
      link: '',
      isExisting: false,
      fileRef: file,
    }));
    setAttachmentList([...attachmentList, ...newDisplayList]);
    e.target.value = '';
  };

  const handleRemoveAttachment = (item, index) => {
    const updatedFileList = [...attachmentList];
    updatedFileList.splice(index, 1);
    setAttachmentList(updatedFileList);

    if (item.isExisting && item.id) {
      setRemovedAttachmentIds([...removedAttachmentIds, item.id]);
    } else {
      const fileIndex = attachmentFiles.findIndex((f) => f === item.fileRef || f.name === item.name);
      if (fileIndex !== -1) {
        const updatedFiles = [...attachmentFiles];
        updatedFiles.splice(fileIndex, 1);
        setAttachmentFiles(updatedFiles);
      }
    }
  };

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append('project_id', props.id);

    reportItems.forEach((item) => {
      formData.append('date', item?.date || '');
      formData.append('purpose', item?.purpose || '');
      formData.append('event', item?.event || '');
    });

    for (let i = 0; i < attachmentFiles.length; i++) {
      formData.append('attachment_files', attachmentFiles[i]);
    }

    CreateSiteVisitReport(formData).then((res) => {
      if (res.status === 200 || res.data?.status === 200 || res.data?.status == 1) {
        Swal.fire({
          icon: 'success',
          title: 'Your work has been saved',
          showConfirmButton: false,
          timer: 1500,
        });
        setIsModalOpen(false);
        fetchSiteVisits(statusFilter);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: res.data?.description || res.data?.error || 'Failed to save site visit report',
          confirmButtonColor: '#23303D',
        });
      }
    });
  };

  const handleUpdateReport = () => {
    const formData = new FormData();
    formData.append('report_log_id', reportLogId);

    reportItems.forEach((item) => {
      const isExistingRecord = item.report_id && Number(item.report_id) > 0;
      const reportId = isExistingRecord ? item.report_id : -1;
      formData.append('report_id', reportId);
      formData.append('date', item?.date || '');
      formData.append('purpose', item?.purpose || '');
      formData.append('event', item?.event || '');
    });

    if (removedAttachmentIds.length > 0) {
      formData.append('attachments_to_remove', JSON.stringify(removedAttachmentIds));
    }

    if (removedReportIds.length > 0) {
      formData.append('reports_to_remove', JSON.stringify(removedReportIds));
    }

    for (let i = 0; i < attachmentFiles.length; i++) {
      formData.append('attachment_files', attachmentFiles[i]);
    }

    UpdateSiteVisitReport(formData).then((res) => {
      if (res.status === 200 || res.data?.status === 200 || res.data?.status == 1) {
        Swal.fire({
          icon: 'success',
          title: 'Your work has been updated',
          showConfirmButton: false,
          timer: 1500,
        });
        fetchSiteVisits(statusFilter);
        setIsModalOpen(false);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: res.data?.description || res.data?.error || res.data?.message || 'Failed to update site visit report',
          confirmButtonColor: '#23303D',
        });
      }
    });
  };

  const handleChangeReportStatus = (selectedReportLogId, method) => {
    const isArchiving = method === 'archive';
    Swal.fire({
      icon: 'question',
      title: `<span style='color:#B42A19;'>${isArchiving ? 'Archive Report?' : 'Restore Report?'}</span>`,
      html: `<p style='font-weight:600;'>Are you sure you want to ${isArchiving ? 'archive' : 'restore'} this report?</p>`,
      showCancelButton: true,
      confirmButtonColor: '#23303D',
      cancelButtonColor: '#B42A19',
      confirmButtonText: isArchiving ? 'Yes, archive' : 'Yes, restore',
    }).then(async (result) => {
      if (result.isConfirmed) {
        const payload = {
          project_id: Number(props.id),
          report_log_id: selectedReportLogId,
          is_status: isArchiving ? 1 : 0,
        };

        try {
          const res = await ArchiveSiteVisitReport(payload);
          if (res.status === 200 || res.data?.status === 200 || res.data?.status == 1) {
            Swal.fire({
              icon: 'success',
              title: isArchiving ? 'Report has been archived' : 'Report has been restored',
              showConfirmButton: false,
              timer: 1500,
            });
            fetchSiteVisits(statusFilter);
          } else {
            Swal.fire({
              icon: 'error',
              title: res.data?.response || 'Error',
              text: res.data?.description || 'Failed to update report status',
              confirmButtonColor: '#23303D',
            });
          }
        } catch (error) {
          console.error('Something went wrong. Please try again', error);
        }
      }
    });
  };

  const isFormValid =
    reportItems.length > 0 &&
    reportItems.every((item) => !!item.date?.trim() && !!item.purpose?.trim() && !!item.event?.trim());

  return (
    <ReportLayout
      title="Site Visit Schedule"
      addButtonLabel="Add New Site Visit"
      onAddNew={handleOpenAddModal}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Search site visits by date or author..."
      statusFilter={statusFilter}
      onStatusFilterChange={(val) => {
        setStatusFilter(val);
        fetchSiteVisits(val);
      }}
      columns={[
        { key: 'date', label: 'Date' },
        { key: 'submitted', label: 'Submitted By' },
        { key: 'last_modified', label: 'Last Modified', className: 'text-aimhi-textlight-muted' },
      ]}
      data={siteVisits}
      isLoading={isLoading}
      emptyMessage="No site visit reports found"
      emptySubMessage={
        searchTerm
          ? 'Try clearing your search query'
          : statusFilter === 0
            ? 'Create your first site visit schedule to get started'
            : 'No archived reports available'
      }
      onView={(report) => handleOpenReportDetails(report.report_log_id, 'View')}
      onEdit={(report) => handleOpenReportDetails(report.report_log_id, 'Edit')}
      onArchive={(report) => handleChangeReportStatus(report.report_log_id, 'archive')}
      onRestore={(report) => handleChangeReportStatus(report.report_log_id, 'restore')}
      isModalOpen={isModalOpen}
      onCloseModal={() => setIsModalOpen(false)}
      modalMode={modalMode}
      modalTitle={
        modalMode === 'Add'
          ? 'New Site Visit Schedule'
          : modalMode === 'Edit'
            ? 'Edit Site Visit Schedule'
            : 'Site Visit Schedule Details'
      }
      modalSubtitle={
        modalMode === 'View'
          ? 'Review recorded visit date, purpose, event, and attachments.'
          : 'Fill in the site visit details and optional attachments.'
      }
      renderModalContent={() => (
        <>
          {weatherData && weatherData.length > 0 && (
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-xs font-semibold text-aimhi-textlight-neutral mb-2">
                Weather Conditions
              </div>
              <div className="flex flex-wrap gap-2">
                {weatherData.map((weather, idx) => (
                  <div
                    key={idx}
                    className="inline-flex items-center gap-2 px-2.5 py-1 text-xs rounded-md bg-white border border-gray-200 text-aimhi-textlight-neutral shadow-xs"
                  >
                    <span className="text-aimhi-textlight-muted">{weather.time}</span>
                    <span className="font-semibold text-aimhi-info">{weather.weather_status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="overflow-x-auto border border-gray-200 rounded-lg mb-4">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-aimhi-light border-b border-gray-200 text-[11px] font-semibold text-aimhi-textlight-neutral uppercase">
                  <th className="py-2.5 px-3 min-w-[150px]">
                    Date <span className="text-aimhi-danger">*</span>
                  </th>
                  <th className="py-2.5 px-3 min-w-[200px]">
                    Primary Purpose <span className="text-aimhi-danger">*</span>
                  </th>
                  <th className="py-2.5 px-3 min-w-[220px]">
                    Major Event <span className="text-aimhi-danger">*</span>
                  </th>
                  {modalMode !== 'View' && <th className="py-2.5 px-3 w-12 text-center">Action</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-xs">
                {reportItems.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50/50">
                    <td className="py-2 px-3 align-top">
                      <input
                        type="date"
                        value={item.date || ''}
                        disabled={modalMode === 'View'}
                        onChange={(e) => handleDateChange(e, index)}
                        className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-300 bg-white text-aimhi-textlight-neutral focus:outline-none focus:ring-2 focus:ring-aimhi-info/20 focus:border-aimhi-info disabled:bg-gray-100 disabled:text-gray-500"
                      />
                    </td>
                    <td className="py-2 px-3 align-top">
                      <input
                        type="text"
                        placeholder="e.g. Regular Inspection"
                        value={item.purpose || ''}
                        disabled={modalMode === 'View'}
                        onChange={(e) => handlePurposeChange(e, index)}
                        className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-300 bg-white text-aimhi-textlight-neutral placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-aimhi-info/20 focus:border-aimhi-info disabled:bg-gray-100 disabled:text-gray-500"
                      />
                    </td>
                    <td className="py-2 px-3 align-top">
                      <input
                        type="text"
                        placeholder="e.g. Safety briefing completed"
                        value={item.event || ''}
                        disabled={modalMode === 'View'}
                        onChange={(e) => handleEventChange(e, index)}
                        className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-300 bg-white text-aimhi-textlight-neutral placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-aimhi-info/20 focus:border-aimhi-info disabled:bg-gray-100 disabled:text-gray-500"
                      />
                    </td>
                    {modalMode !== 'View' && (
                      <td className="py-2 px-3 align-middle text-center">
                        {reportItems.length > 1 && (
                          <button
                            type="button"
                            title="Remove row"
                            onClick={() => handleRemoveReportRow(index)}
                            className="p-1 rounded text-gray-400 hover:text-aimhi-danger hover:bg-aimhi-danger/10 transition-colors cursor-pointer"
                          >
                            <FaTrash className="text-xs" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {modalMode !== 'View' && (
            <div className="mb-4">
              <button
                type="button"
                onClick={handleAddReportRow}
                disabled={!isFormValid}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-aimhi-textlight-neutral hover:underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-0 p-0"
              >
                <FaPlus className="text-[10px]" />
                <span>Add More Visit</span>
              </button>
            </div>
          )}
        </>
      )}
      enableAttachments
      attachments={attachmentList}
      onUploadFiles={handleUploadFiles}
      onRemoveAttachment={handleRemoveAttachment}
      attachmentFileInputId="site-visit-file-input"
      isFormValid={isFormValid}
      onSubmit={handleSubmit}
      onUpdate={handleUpdateReport}
    />
  );
};

export default SiteVisitV2;
